import { useContext } from 'react';

import { getI18nData, isBrowser, useProductUrls } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import { LayoutContext } from '@lucid/components-react';
import { type Drupal_Entity } from 'generated/drupal-graphql';
import { type CustomDrupal } from 'types/types';

const visioQueryParams = '?type=visio';
const discoveryQueryParams = '?type=discovery';

export const placeholderButtons: CustomDrupal.LinkInterface[] = [
  {
    title: 'Sign up free',
    link: {
      url: {
        path: '#placeholder',
      },
    },
    links: [],
    type: 'primary',
    secondary: true,
    isButton: true,
    isPlaceholder: true,
    entityId: 'placeholder',
  },
];

export const placeholderLinks: CustomDrupal.LinkInterface[] = [
  {
    title: 'Log in',
    link: {
      url: {
        path: '#placeholder-link',
      },
    },
    links: [],
    type: 'primary',
    primary: true,
    isButton: false,
    isPlaceholder: true,
    entityId: 'placeholder-link',
  },
];

const pricingUrlQueryParams = (pricingUrl: string, isVisio: boolean, isDiscovery: boolean) => {
  if (isVisio) {
    return `${pricingUrl}${visioQueryParams}`;
  }

  if (isDiscovery) {
    return `${pricingUrl}${discoveryQueryParams}`;
  }

  return pricingUrl;
};

const useNavButtons = (
  username?: string,
  isVisio = false,
  isDiscovery = false,
  shouldHideSignUpButton = false,
): {
  superNavButtons: CustomDrupal.LinkInterface[];
  navButtons: CustomDrupal.LinkInterface[];
} => {
  const {
    language: { urlLangPrefix, i18nKey, shortLangPrefix },
  } = useContext(LayoutContext);
  const {
    i18nData,
    drupal: {
      nodeById: { entityTranslations },
    },
  } = useStaticQuery(graphql`
    {
      drupal {
        nodeById(id: "4361") {
          entityTranslations {
            entityLanguage {
              id
            }
            entityUrl {
              path
            }
          }
        }
      }
      i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
        edges {
          node {
            name
            childSrcJson {
              mktg_nav_user_settings
              mktg_nav_log_out
              mktg_nav_documents
              mktg_nav_sign_up_free
              mktg_nav_log_in
              mktg_nav_account
              mktg_nav_contact_sales
            }
          }
        }
      }
    }
  `);

  const { pricingUrl, productUrl } = useProductUrls(urlLangPrefix);

  if (!isBrowser()) {
    return {
      superNavButtons: [],
      navButtons: [],
    };
  }

  const i18n = getI18nData(i18nData, i18nKey);
  const topLevelInitialLoggedInButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-log-out'),
      link: {
        url: {
          path: `${productUrl}/${urlLangPrefix}users/logout`,
        },
      },
      links: [],
      fieldBeaconElementDetail: 'log-out',
      entityId: 'log-out',
    },
  ];

  const topLevelInitialLoggedOutButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-log-in'),
      link: {
        url: {
          path: `${productUrl}/${urlLangPrefix}users/login?activate=lucidchart`,
        },
      },
      links: [],
      fieldBeaconElementDetail: 'log-in',
      entityId: 'login',
    },
  ];

  const loggedInButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-documents'),
      link: {
        url: {
          path: `${productUrl}/documents`,
        },
      },
      links: [],
      type: 'primary',
      primary: true,
      fieldBeaconElementDetail: 'documents',
      entityId: 'documents',
      isButton: true,
    },
  ];

  const contactSalesCtaPath = entityTranslations.find(
    (translation: Drupal_Entity) => translation?.entityLanguage?.id?.includes(shortLangPrefix),
  );

  const initialLoggedOutButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-contact-sales'),
      link: {
        url: {
          path: contactSalesCtaPath?.entityUrl?.path,
        },
      },
      links: [],
      fieldBeaconElementDetail: 'contact-sales',
      entityId: 'contact-sales',
      entityUuid: 'contact-sales',
      parent: null,
      entityTranslations: [],
    },
    {
      title: i18n('mktg-nav-sign-up-free'),
      link: {
        url: {
          path: pricingUrlQueryParams(pricingUrl, isVisio, isDiscovery),
        },
      },
      links: [],
      type: 'primary',
      primary: true,
      fieldBeaconElementDetail: 'sign-up-free',
      entityId: 'sign-up-free',
      isButton: true,
    },
  ];

  let loggedOutButtons = initialLoggedOutButtons;
  if (shouldHideSignUpButton) {
    loggedOutButtons = initialLoggedOutButtons.slice(0, 1);
  }

  return username
    ? {
        superNavButtons: topLevelInitialLoggedInButtons,
        navButtons: loggedInButtons,
      }
    : {
        superNavButtons: topLevelInitialLoggedOutButtons,
        navButtons: loggedOutButtons,
      };
};

export default useNavButtons;
